// RPR-6932: every fetch below needs a rejection handler, and the pollers need to be idempotent.
//
// HttpAuth re-throws everything except the one 403 whose detail is 'Authentication credentials
// were not provided.' (handle403, app_common.js), so a fetch against an unreachable API rejects
// with {status: -1, xhrStatus: 'error'}. Left dangling, $q hands that to $exceptionHandler, which
// app_common.js decorates to call Sentry.captureException - the "Possibly unhandled rejection"
// issue on the RPR-PORTAL-YT Sentry project.
//
// Handlers use the two-argument .then(success, onRejected) form rather than a trailing .catch().
// A trailing .catch() also swallows exceptions thrown by the success handler, and the mapping in
// fetchPayers dereferences payer.country.flag - a null country would stop reaching Sentry and
// surface as an empty "Something went wrong" dialog. Rejections are handled here; genuine bugs
// keep propagating.
//
// The polled stats report rather than display. portalHelper.showErrorCommon is built for
// user-initiated requests: it clears the user's toasts, can reload the page through the wrong-user
// retry, and latches its offline dialog so the user's own next failure would be suppressed.
// Putting a 30-second timer in front of that trades a Sentry flood for a worse user-facing bug.
//
// The services are singletons, but the header controllers that start the pollers sit under an
// ng-if on login state (header_admin.3dcc959a.html), so they are destroyed and rebuilt whenever that flips,
// and Angular does not cancel an $interval when the scope that created it is destroyed.

const STATS_POLL_FAILURES_BEFORE_REPORT = 3;

// Quiet through a blip, one Sentry event once a poll has been failing long enough to mean
// something, re-armed on the next success. Fully silent would leave a permanently broken stats
// endpoint indistinguishable from a healthy one, which is the trap this ticket exists to avoid.
function reportStatsPollFailure(service, label, response) {
  service.consecutivePollFailures = (service.consecutivePollFailures || 0) + 1;
  if (service.consecutivePollFailures !== STATS_POLL_FAILURES_BEFORE_REPORT) {
    return;
  }
  if (globalThis.Sentry) {
    globalThis.Sentry.captureMessage(
      'RPR-6932 ' + label + ' failed ' + STATS_POLL_FAILURES_BEFORE_REPORT + ' consecutive polls',
      {level: 'warning', extra: {status: response?.status, xhrStatus: response?.xhrStatus}}
    );
  }
}

/**
 * Registers one of the header stats services.
 *
 * The admin, client and payer services differ only in endpoint and Sentry label, so they share a
 * single definition rather than three hand-copied bodies. That copying is what let the missing
 * rejection handler survive in three places at once, and it is what SonarCloud reports as
 * duplication on this file.
 */
function defineSurveyStatsService(serviceName, statsPath, label) {
  app.service(serviceName, ['$rootScope', 'HttpAuth', 'apiHelper', '$interval',
    function ($rootScope, HttpAuth, apiHelper, $interval) {
      const service = this;
      service.statsUrl = apiHelper.getApiUrl() + statsPath;

      service.pollSurveyStats = function () {
        if (service.pollHandle) {
          return service.pollHandle;
        }
        service.pollHandle = $interval(
          function () {
            // Fetch stats every 30 seconds.
            service.fetchStats();
          }
          , 30000);
        return service.pollHandle;
      };

      service.stopPollingSurveyStats = function () {
        if (service.pollHandle) {
          $interval.cancel(service.pollHandle);
          service.pollHandle = null;
        }
      };

      // invalidate forces the backend to recompute rather than serve its cached figures. Only the
      // admin dashboard passes it (admin_dashboard.js); the polled callers never do.
      service.fetchStats = function (invalidate) {
        const invalidate_cache = invalidate ? '?invalidate=true' : '';
        return HttpAuth.get(service.statsUrl + invalidate_cache).then(
          function (result) {
            service.consecutivePollFailures = 0;
            $rootScope.rootscope_header_surveyStats = result.data;
          },
          function (error_) {
            reportStatsPollFailure(service, label, error_);
          }
        );
      };
    }]);
}

defineSurveyStatsService('surveyAdminStatsService', '/api/admin/stats/', 'admin stats');
defineSurveyStatsService('surveyClientStatsService', '/api/client/survey/stats/', 'client stats');
defineSurveyStatsService('surveyPayerStatsService', '/api/payer/survey/stats/', 'payer stats');


/**
 * Provides services to fetch data for header
 */
app.service('menuHeaderService', ['$rootScope', 'HttpAuth', 'apiHelper', 'portalHelper',
  function ($rootScope, HttpAuth, apiHelper, portalHelper) {
    const menuHeaderService = this;

    // Fetch all payers details
    menuHeaderService.fetchPayers = function () {
      const payerListUrl = apiHelper.getApiUrl() + '/api/admin/payer.json?view_type=n';
      return HttpAuth.get(payerListUrl).then(
        function (result) {
          $rootScope.rootscope_header_PayersList = result.data;

          $rootScope.rootscope_header_PayersList = $rootScope.rootscope_header_PayersList.filter(
            function (payer) {
              return payer.full_name && payer.active && !payer.locked;
            }
          );
          $rootScope.rootscope_header_PayersList = _.map($rootScope.rootscope_header_PayersList,
            function (payer) {
              return {
                'id': payer.id,
                'full_name': payer.full_name,
                'country_flag': payer.country.flag,
                'country_name': payer.country.name
              };
            }
          );
        },
        portalHelper.showErrorCommon
      );
    };

    // Fetch all clients details.
    //
    // Unlike fetchPayers above, this one does NOT handle its own rejection: it resolves with the
    // client list, which surveyMenu consumes, so a rejection handler here would resolve callers
    // with undefined instead. Every caller must therefore attach its own - see
    // admin_header_controller.js and directives/survey_menu.js. RPR-6932.
    menuHeaderService.fetchClients = function () {
      const clientListUrl = apiHelper.getApiUrl() + '/api/admin/client/user.json';
      return HttpAuth.get(clientListUrl).then(
        function (result) {
          $rootScope.rootscope_header_ClientsList = result.data;

          $rootScope.rootscope_header_ClientsList = $rootScope.rootscope_header_ClientsList.filter(
            function (client) {
              return client.first_name && client.last_name && client.active && !client.locked;
            }
          );

          $rootScope.rootscope_header_ClientsList = _.map($rootScope.rootscope_header_ClientsList,
            function (client) {
              const updatedClientFields = {
                'id': client.id,
                'full_name' : client.first_name + ' ' + client.last_name,
                'country_name' : client.country_name,
                'country_flag' : client.country_flag
              };

              const updatedClient = Object.assign(client, updatedClientFields);
              return updatedClient;
            }
          );

          return $rootScope.rootscope_header_ClientsList;
        }
      );
    };

    menuHeaderService.fetchPayerExpertiseFormTypes = function () {

      if (menuHeaderService.payerExpertiseFormTypes) {
        return Promise.resolve(menuHeaderService.payerExpertiseFormTypes);
      }

      return HttpAuth.get(
        apiHelper.getApiUrl() + '/api/admin/payer_form.json'
      ).then(
        function (response) {
          menuHeaderService.payerExpertiseFormTypes = response.data;
          return menuHeaderService.payerExpertiseFormTypes;
        },
        function (error) {
          portalHelper.showErrorCommon(error);
        }
      );

    };


  }]);
